import React, {useEffect, useRef, useState} from "react";
import {Combobox} from "@headlessui/react";
import {SelectorIcon} from "@heroicons/react/solid";
import FieldHint from "../Field/FieldHint";
import ComboboxToken from "./ComboboxToken";
import ComboboxOptionList, {Option, OptionId} from "./ComboboxOptionList";
import Spinner from "../../../Spinner";
import {useDebounce} from "../../../../hooks/useDebounce";

// Helpers ----

function withoutOption(options: Array<Option>, id: OptionId): Array<Option> {
  return [...options].filter(selectedOption => selectedOption.id !== id)
}

function withoutLastOption(options: Array<Option>): Array<Option> {
  const newOptions = [...options]
  newOptions.pop()

  return newOptions
}

// Component ----

export type MultiComboboxProps = {
  label?: string
  hint?: string
  placeholder?: string
  options: Array<Option>
  selected?: Array<Option>
  onSearch?: (searchTerm?: string) => void
  onChange: (options: Array<Option>) => any
  isLoading?: boolean
}

function MultiCombobox({label, hint, placeholder, options, selected, onSearch, onChange, isLoading}: MultiComboboxProps) {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce<string>(query, 500)

  useEffect(() => {
    if (onSearch && debouncedQuery) {
      onSearch(debouncedQuery)
    }
  }, [onSearch, debouncedQuery]);

  const [selectedOptions, setSelectedOptions] = useState<Array<Option>>(selected ?? []);

  if (selected !== undefined && selectedOptions !== selected) {
    setSelectedOptions(selected)
  }

  const inputRef = useRef<HTMLInputElement>(null)

  const filteredOptions = (!!onSearch || query === '') ? options : options.filter(option => {
    const terms = query.toLowerCase().split(' ').filter(Boolean)
    const optionName = option.name.toLowerCase()

    return terms.every(term => optionName.includes(term))
  })

  function handleChange(options: Array<Option>) {
    setSelectedOptions(options)
    onChange(options)
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Backspace') return

    // Determine the current cursor position and whether any text has been selected
    const selectionStart = Number(event.currentTarget.selectionStart)
    const selectionEnd = Number(event.currentTarget.selectionEnd)
    const selectionLength = selectionEnd - selectionStart

    // If the cursor is at the start of the input, and no text has been selected, remove the last selected option
    if (selectionStart === 0 && selectionLength === 0) {
      handleChange(
        withoutLastOption(selectedOptions)
      )
    }
  }

  function handleDeselectOption(option: Option) {
    handleChange(
      withoutOption(selectedOptions, option.id)
    )
  }

  return (
    <Combobox as="div" value={selectedOptions} onChange={handleChange} nullable={true} multiple={true}>

      {/* Field Header */}
      <div className="flex justify-between">
        {/* Label */}
        {!!label &&
          <Combobox.Label className="block text-sm font-medium leading-6 text-gray-700">{label}</Combobox.Label>}

        {/* Hint */}
        {!!hint && <FieldHint>{hint}</FieldHint>}
      </div>

      {/* Container */}
      <div className="relative mt-2">

        <div
          onClickCapture={() => inputRef.current?.focus()}
          className="px-1 py-0.5 flex flex-wrap bg-white pr-9 rounded-md shadow-sm ring-1 ring-gray-300 ring-inset focus-within:ring-2 focus-within:ring-brand"
        >
          {/* Token List */}
          {selectedOptions.map(option => (
            <ComboboxToken
              key={option.id}
              icon={option.icon}
              onRemove={() => handleDeselectOption(option)}
            >
              {option.name}
            </ComboboxToken>
          ))}

          {/* Main Input */}
          <Combobox.Input
            className="w-full flex-1 shrink-0 basis-1/2 lg:basis-1/3 rounded-md border-0 p-0 bg-white py-1.5 pl-2 pr-10 text-gray-900 sm:text-sm sm:leading-6 focus:ring-0 "
            placeholder={placeholder ?? 'Type to find...'}
            onKeyDown={handleKeyDown}
            onChange={event => setQuery(event.target.value)}
            displayValue={() => ''}
            ref={inputRef}
          />
        </div>

        {/* Open list button */}

        {!!isLoading ?
          <div className="absolute inset-y-0 right-0 flex items-center px-2 focus:outline-none">
            <Spinner color={Spinner.colors.DARK} size={Spinner.sizes.X_SMALL}/>
          </div>
          :
          <Combobox.Button
            className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            <SelectorIcon className="h-5 w-5 text-gray-400" aria-hidden="true"/>
          </Combobox.Button>
        }

        {filteredOptions.length > 0 && <ComboboxOptionList options={filteredOptions}/>}
      </div>
    </Combobox>
  )
}

export default MultiCombobox;